import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import PlanSelectionForm from './components/PlanSelectionForm';
import PersonalDataForm from './components/PersonalDataForm';
import Descargar from './pages/Descargar';
import './styles/App.css';
import logo from './assets/images/Logo_Colegio_Ingenieros.png';

function App() {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [personalData, setPersonalData] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [isCompleted, setIsCompleted] = useState(false);

  const planOptions = [
    { value: 'titular-sin-cargas', label: 'Titular sin cargas', description: 'Cobertura individual' },
    { value: 'titular-1-carga', label: 'Titular + 1 carga', description: 'Cobertura para 2 personas' },
    { value: 'titular-2-cargas', label: 'Titular + 2 cargas', description: 'Cobertura para 3 personas' },
    { value: 'titular-3-cargas', label: 'Titular + 3 cargas', description: 'Cobertura para 4 personas' },
    { value: 'titular-4-mas-cargas', label: 'Titular + 4 o más cargas', description: 'Cobertura para 5 o más personas' }
  ];

  const getPlanLabel = (planValue) => {
    const plan = planOptions.find(option => option.value === planValue);
    return plan ? `${plan.label} - ${plan.description}` : planValue;
  };

  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('es-CL');
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setCurrentStep(2);
    console.log('Plan seleccionado en App:', plan);
  };

  const handlePersonalDataSubmit = (data) => {
    setPersonalData(data);
    setCurrentStep(3);
    console.log('Datos personales:', data);
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinalConfirmation = async () => {
    try {
      // Importar dinámicamente el servicio
      const { default: ContactoService } = await import('./services/contactoService');
      
      // Preparar los datos para enviar al backend
      const contactoData = ContactoService.formatContactoData(personalData, selectedPlan);
      
      // Enviar datos al backend
      const response = await ContactoService.crearContacto(contactoData);
      
      if (response.success) {
        setIsCompleted(true);
        setCurrentStep(4);
        console.log('Pre-inscripción guardada exitosamente:', response);
      } else {
        throw new Error(response.message || 'Error al guardar la pre-inscripción');
      }
    } catch (error) {
      console.error('Error al confirmar pre-inscripción:', error);
      alert('Error al guardar la información: ' + error.message);
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <PlanSelectionForm 
            onPlanSelect={handlePlanSelect}
            initialPlan={selectedPlan}
          />
        );
      case 2:
        return (
          <PersonalDataForm 
            onDataSubmit={handlePersonalDataSubmit}
            onBack={handleBack}
            initialData={personalData}
            planSeleccionado={selectedPlan}
          />
        );
      case 3:
        return (
          <div className="confirmation-container">
            <div className="form-header">
              <h1>Confirmación de Pre-inscripción</h1>
              <p>Revise la información ingresada antes de confirmar su pre-inscripción</p>
            </div>
            <div className="summary">
              <div className="summary-section">
                <h3>Plan Seleccionado</h3>
                <div className="summary-item">
                  <span className="summary-value">{getPlanLabel(selectedPlan)}</span>
                  <button 
                    className="edit-btn"
                    onClick={() => setCurrentStep(1)}
                  >
                    Editar
                  </button>
                </div>
              </div>

              {personalData && (
                <div className="summary-section">
                  <h3>Datos del Titular</h3>
                  <div className="summary-item">
                    <div className="summary-data">
                      <p><strong>Nombre:</strong> {personalData.nombreCompleto}</p>
                      <p><strong>RUT:</strong> {personalData.rut}</p>
                      <p><strong>Fecha de Nacimiento:</strong> {formatDate(personalData.fechaNacimiento)}</p>
                      <p><strong>Correo:</strong> {personalData.correo}</p>
                      <p><strong>Celular:</strong> {personalData.celular}</p>
                      <p><strong>¿Eres Soci@ del Colegio de Ingenieros?:</strong> {personalData.esColegiado === 'S' ? 'Sí' : 'No'}</p>
                      <p><strong>¿Se encuentra al día en el pago de la colegiatura?:</strong> {personalData.colegiaturaAlDia === 'S' ? 'Sí' : 'No'}</p>
                      {personalData.comentarios && (
                        <p><strong>Comentarios:</strong> {personalData.comentarios}</p>
                      )}
                    </div>
                    <button 
                      className="edit-btn"
                      onClick={() => setCurrentStep(2)}
                    >
                      Editar
                    </button>
                  </div>
                </div>
              )}

              {personalData && personalData.cargas && personalData.cargas.length > 0 && (
                <div className="summary-section">
                  <h3>Cargas ({personalData.cargas.filter(c => c.rut || c.nombres || c.fechaNacimiento).length})</h3>
                  <div className="summary-item">
                    <div className="summary-data">
                      {personalData.cargas
                        .filter(carga => carga.rut || carga.nombres || carga.fechaNacimiento)
                        .map((carga, index) => (
                          <div key={index} className="carga-summary">
                            <strong>Carga {index + 1}:</strong>
                            <p>• Nombres: {carga.nombres}</p>
                            <p>• RUT: {carga.rut}</p>
                            <p>• Fecha Nac.: {formatDate(carga.fechaNacimiento)}</p>
                          </div>
                        ))
                      }
                    </div>
                    <button 
                      className="edit-btn"
                      onClick={() => setCurrentStep(2)}
                    >
                      Editar
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="confirmation-actions">
              <button 
                className="back-btn"
                onClick={() => setCurrentStep(2)}
              >
                Volver
              </button>
              <button 
                className="confirm-btn"
                onClick={handleFinalConfirmation}
              >
                Confirmar Pre-inscripción
              </button>
            </div>
          </div>
        );
      case 4:
        return (
          <div className="success-container">
            <div className="form-header">
              <h1>¡Pre-inscripción Completada!</h1>
              <p>Gracias por completar su pre-inscripción al seguro complementario de salud</p>
            </div>
            <div className="success-message">
              <p>Su solicitud ha sido recibida correctamente. En breve nos contactaremos con usted.</p>
            </div>
            <button 
              className="restart-btn"
              onClick={() => {
                setCurrentStep(1);
                setSelectedPlan(null);
                setPersonalData(null);
                setIsCompleted(false);
              }}
            >
              Nueva Inscripción
            </button>
          </div>
        );
      default:
        return (
          <PlanSelectionForm 
            onPlanSelect={handlePlanSelect}
            initialPlan={selectedPlan}
          />
        );
    }
  };

  const MainFormulario = () => (
    <>
      {currentStep < 4 && (
        <div className="progress-indicator">
          <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>1. Selección de Plan</div>
          <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>2. Datos Personales</div>
          <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>3. Confirmación</div>
        </div>
      )}

      <main className="main-content">
        {renderCurrentStep()}
      </main>
    </>
  );

  return (
    <Router basename="/pre-inscripcion">
      <div className="App">
        <header className="app-header">
          <img src={logo} alt="Colegio de Ingenieros" className="app-logo" />
          <div className="app-title">
            <h1>Seguro Complementario de Salud</h1>
            <p>Pre-inscripción - Colegio de Ingenieros</p>
          </div>
        </header>

        <Routes>
          <Route path="/" element={<MainFormulario />} />
          <Route path="/descargar" element={<Descargar />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;